import React, { useRef, useState } from 'react';
import { Autocomplete, Chip, CircularProgress, TextField } from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { searchTagsForFilter } from '../../../../../services/tags.service';
import { addChatTag, removeChatTag } from '../../../../../services/2talk/omnichannel';

// Classificação da conversa por tags (on-the-fly): chips com remover (x) + autocomplete freeSolo que
// sugere o catálogo do hirer (GET /tags/autocomplete) e permite CRIAR tag nova ao digitar + Enter.
// Props: chatId | tags [{id,name}] atuais | onChanged() recarrega após add/remove | intl.
// Sem async/await em arrow (babel 7.12.3 do react-scripts 4) — Promise chain.
export default function ChatTagsEditor({ chatId, tags, onChanged, intl }) {
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  const list = Array.isArray(tags) ? tags : [];

  const runSearch = (value) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 3) { setOptions([]); return; }
    debounceRef.current = setTimeout(() => {
      setLoadingSearch(true);
      searchTagsForFilter(value)
        .then(res => {
          if (res && res.data && res.data.success && Array.isArray(res.data.data)) {
            const applied = new Set(list.map(t => (t.name || '').toLowerCase()));
            setOptions(res.data.data.filter(o => !applied.has((o.name || '').toLowerCase())));
          } else {
            setOptions([]);
          }
        })
        .catch(() => setOptions([]))
        .finally(() => setLoadingSearch(false));
    }, 800);
  };

  const addByName = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed || !chatId || busy) return;
    // Dedup local: já aplicada -> só limpa o input.
    if (list.some(t => (t.name || '').toLowerCase() === trimmed.toLowerCase())) {
      setInputValue(''); setSearch(''); setOptions([]);
      return;
    }
    setBusy(true);
    addChatTag(chatId, trimmed)
      .then(result => {
        setInputValue(''); setSearch(''); setOptions([]);
        if (result && result.success && onChanged) onChanged();
      })
      .finally(() => setBusy(false));
  };

  const handleRemove = (tag) => {
    if (!tag || !tag.id || !chatId || busy) return;
    setBusy(true);
    removeChatTag(chatId, tag.id)
      .then(result => { if (result && result.success && onChanged) onChanged(); })
      .finally(() => setBusy(false));
  };

  const handleChange = (_, value) => {
    if (!value) return;
    // freeSolo: string (tag nova digitada) OU objeto {id,name} (selecionada do catálogo).
    addByName(typeof value === 'string' ? value : (value.name || ''));
  };

  return (
    <div>
      {list.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {list.map((t, idx) => (
            <Chip
              key={t.id ?? t.name ?? idx}
              label={t.name}
              size="small"
              icon={<LocalOfferOutlinedIcon style={{ fontSize: 14 }} />}
              onDelete={busy ? undefined : () => handleRemove(t)}
              sx={{ borderRadius: '6px', backgroundColor: '#eef3f8', color: '#37474f', fontWeight: 500, maxWidth: '100%' }}
            />
          ))}
        </div>
      )}
      <Autocomplete
        freeSolo
        size="small"
        value={null}
        blurOnSelect
        clearOnBlur
        handleHomeEndKeys
        disabled={busy}
        options={options}
        loading={loadingSearch}
        inputValue={inputValue}
        onInputChange={(_, v) => { setInputValue(v); runSearch(v); }}
        onChange={handleChange}
        getOptionLabel={(o) => (typeof o === 'string' ? o : (o.name || ''))}
        filterOptions={(x) => x}
        noOptionsText={
          search.length < 3
            ? intl.formatMessage({ id: "chat.tags.searchhint" })
            : (loadingSearch ? intl.formatMessage({ id: "chat.tags.loading" }) : intl.formatMessage({ id: "chat.tags.notfound" }))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={intl.formatMessage({ id: "chat.tags.add.placeholder" })}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {(loadingSearch || busy) ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </div>
  );
}
